//Imports
import React, { useState, useEffect, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from 'uuid';
import { db } from "../../../firebase";
import { addDoc, collection, doc, updateDoc } from "firebase/firestore";
//Import Context
import { AppContext } from "../../../contexts/AppContext";
//Imports Components
import AddPMEActivity from "./AddPMEActivity";
import ErrorPopUp, { DeleteConfirmationPopUp, ErrorEditPopUp } from "./ErrorPopUp";
import MessageConfirmation from "../../messageConfirmation/MessageConfirmation";
import Loading from "../../../shared/ui-loading/Loading";
//Imports Helpers
import YearSelect from "../../../shared/YearSelect";
import { formatCurrency, getSubventionNames } from "../../../shared/helpers";
//Imports Icons
import trash from '../../../assets/icons/delete.svg';
import edit from '../../../assets/icons/edit.svg';
import view from '../../../assets/icons/visible.svg';
import arrowSide from '../../../assets/icons/arrowSide.svg';
import addPME from '../../../assets/icons/pmeWhite.svg';
//Imports CSS
import './pmeForm.css';

function PMEForm() {
    const { establishmentList, dataProjects, pmeList, requestsList, globalNotifications, showNotification, setDataSearch } = useContext(AppContext);
    const [pmeData, setPmeData] = useState(null);
    const [initialPmeData, setInitialPmeData] = useState(null);
    const [showAddPmeActivities, setShowPmeActivities] = useState(false);
    const [editingActivity, setEditingActivity] = useState(null);
    const [viewingActivity, setViewingActivity] = useState(null);
    const [error, setError] = useState(false);
    const [confirmation, setConfirmation] = useState(false);
    const [yearExistsWarning, setYearExistsWarning] = useState(false);
    const [existingPmeId, setExistingPmeId] = useState(null);
    const [showErrorDelete, setShowErrorDelete] = useState(false);
    const [showErrorEdit, setShowErrorEdit] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [activityDelete, setActivityDelete] = useState(null);

    const navigate = useNavigate();
    const location = useLocation();
    const { establishmentId, viewMode, pme, dataSearch } = location.state;
console.log(dataSearch);
    useEffect(() => {
        if (pme) {
            setPmeData(deepCopyPme(pme));
            setInitialPmeData(deepCopyPme(pme));
        } else {
            const newPmeData = {
                yearPME: null,
                activities: [],
                establishmentId: establishmentId,
            };
            setPmeData(newPmeData);
            setInitialPmeData(deepCopyPme(newPmeData));
        }
    }, [location.state, establishmentId, pme]);

    const calculateTotalAmount = () => {
        return pmeData.activities.reduce((total, activity) => total + activity.totalSubsidyAmount, 0);
    };

    const checkYearExists = (year) => {
        const existingPme = pmeList.find(pme =>
            pme.yearPME === year && pme.establishmentId === establishmentId
        );
        if (existingPme) {
            setYearExistsWarning(true);
            setExistingPmeId(existingPme.id);
        } else {
            setYearExistsWarning(false);
            setExistingPmeId(null);
        }
    };

    const handleYearChange = (year) => {
        checkYearExists(year);
        setPmeData(prevPmeData => ({ ...prevPmeData, yearPME: year }));
    };

    const handleEditExisting = () => {
        setExistingPmeId(null);
        setYearExistsWarning(false)
        navigate(`/establecimiento/pme/${existingPmeId}`, {
            state: {
                pme: pmeList.find(pme => pme.id === existingPmeId),
                viewMode: false,
                establishmentId,
                dataSearch
            }
        });
    };

    const handleCancelExisting = () => {
        setYearExistsWarning(false);
        setExistingPmeId(null);
        setPmeData(prevPmeData => ({ ...prevPmeData, yearPME: null }));
    }

    const handleClickCancel = () => {
        navigate(-1, { state: { dataSearch } });
    };

    const handleOpenForm = () => {
        setShowPmeActivities(true);
    };

    const handleAddOrUpdateActivity = (activityData) => {
        if (editingActivity) {
            setPmeData(prevPmeData => ({
                ...prevPmeData,
                activities: prevPmeData.activities.map(act => act.id === activityData.id ? activityData : act)
            }));
        } else {
            const newActivity = {
                ...activityData,
                id: uuidv4()
            };
            setPmeData(prevPmeData => ({
                ...prevPmeData,
                activities: [...prevPmeData.activities, newActivity]
            }));
        }
        setEditingActivity(null);
    };

    const handleViewActivity = (activity) => {
        setViewingActivity(activity);
        setShowPmeActivities(true);
    };

    const handleEditActivity = (activity) => {
        // Verificar si la actividad tiene solicitudes asociadas
        const hasAssociatedRequests = requestsList.some(request => request.idActivity === activity.id);
        if(hasAssociatedRequests){
            setShowErrorEdit(true);
        } else{
            setEditingActivity(activity);
            setShowPmeActivities(true);
        }
    };

    const handleDeleteActivity = (activity) => {
        // Verificar si la actividad tiene solicitudes asociadas
        const hasAssociatedRequests = requestsList.some(request => request.idActivity === activity.id);
        if (hasAssociatedRequests) {
            setShowErrorDelete(true);
        } else {
            // Si no hay solicitudes asociadas, proceder con mensaje de confirmación
            setShowDeleteConfirmation(true);
            setActivityDelete(activity);
        }
    };
    //Función para confirmar eliminación de actividad
    const confirmDelete = () => {
        setPmeData(prevPmeData => ({
            ...prevPmeData,
            activities: prevPmeData.activities.filter(activity => activity.id !== activityDelete.id)
        }));
        setShowDeleteConfirmation(false);
        setActivityDelete(null);
    };
    //Función para cancelar eliminación de actividad
    const cancelDelete = () => {
        setShowDeleteConfirmation(false);
        setActivityDelete(null);
    };

    const handleCloseForm = () => {
        setShowPmeActivities(false);
        setViewingActivity(null);
        setEditingActivity(null);
    };

    const hasChanges = () => {
        if (!initialPmeData) return false;

        if (pmeData.yearPME !== initialPmeData.yearPME) return true;
        if (pmeData.establishmentId !== initialPmeData.establishmentId) return true;

        if (pmeData.activities.length !== initialPmeData.activities.length) return true;

        for (let i = 0; i < pmeData.activities.length; i++) {
            if (JSON.stringify(pmeData.activities[i]) !== JSON.stringify(initialPmeData.activities[i])) {
                return true;
            }
        }

        return false;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (pme?.id && !hasChanges()) {
            navigate(-1, { state: { dataSearch } });
            return;
        }
        try {
            if (pme?.id) {
                await updateDoc(doc(db, 'pme', pme.id), pmeData);
                console.log('se actualizo doc: ', pme.id);
            } else {
                const docRef = await addDoc(collection(db, 'pme'), pmeData);
                console.log("Document written with ID: ", docRef.id);
            }
            showNotification('addUpdatedPME', true);
            setTimeout(() => {
                navigate(-1, { state: { dataSearch } });
                setDataSearch(dataSearch);
            }, 3000);
        } catch (error) {
            console.error("Error adding/updating document: ", error);
            setError(true);
        }
    };

    if (!pmeData) return <Loading />;

    const selectedEstablishment = establishmentList.find(establishment => establishment.id === establishmentId);

    const selectedProject = selectedEstablishment
        ? dataProjects.find(project => project.id === selectedEstablishment.projectId)
        : null;

    return (
        <section className='container-pme-form flex'>
            <aside className={`overlay flex ${(showAddPmeActivities || yearExistsWarning) && 'show'}`}></aside>
            <section className='container-info-pme flex'>
                {selectedEstablishment && selectedProject && (
                    <article className='box-title flex'>
                        <h3>{selectedProject.nameProject}</h3>
                        <img src={arrowSide} className='icon' alt='Icono de flecha' />
                        <p>{selectedEstablishment.nameEstablishment}</p>
                    </article>
                )}
                {!viewMode ? (
                    <>
                        <YearSelect
                            startYear={2020}
                            selectedYear={pmeData.yearPME}
                            onYearChange={handleYearChange}
                        />
                        {yearExistsWarning && (
                            <aside className="year-exists-warning flex">
                                <p>Ya existe un PME para el año {pmeData.yearPME}.</p>
                                <span className='flex'>
                                    <button className='button' onClick={handleEditExisting}>Editar PME existente</button>
                                    <button className='button' onClick={handleCancelExisting}>Cancelar</button>
                                </span>
                            </aside>
                        )}
                    </>
                ) : (
                    <h3>Año PME: {pmeData.yearPME}</h3>
                )}
            </section>
            {pmeData?.yearPME &&
                <section className='box-pme flex' >
                    <p>{`Plan de Mejoramiento Educativo ${pmeData.yearPME}`} </p>
                    <table className='table-pme flex'>
                        <thead className='header-table flex'>
                            <tr className='flex'>
                                <th>Dimensión</th>
                                <th>Nombre de la Acción</th>
                                <th>Subvención</th>
                                <th>Monto Total</th>
                                <th>Ver</th>
                                <th>Editar</th>
                                <th>Eliminar</th>
                            </tr>
                            <tr className='button'>
                                <th>
                                    <button type='button' className='box-button flex' onClick={handleOpenForm}>
                                        <img src={addPME} className='icon' alt='Icono para crear PME' />
                                        <p>Agregar Nueva Acción</p>
                                    </button>
                                </th>
                            </tr>
                        </thead>
                        <tbody className='body-table flex'>
                            {pmeData.activities.length > 0 ? (
                                pmeData.activities.map(activity => (
                                    <tr key={activity.id} className='flex'>
                                        <td>{activity.dimension}</td>
                                        <td>{activity.nameOfAction}</td>
                                        <td>{getSubventionNames(activity.subsidies)}</td>
                                        <td>{formatCurrency(activity.totalSubsidyAmount)}</td>
                                        <td><img src={view} className='icon' alt='Icono para ver actividad' onClick={() => handleViewActivity(activity)} /></td>
                                        <td><img src={edit} className='icon' alt='Icono para editar actividad' onClick={() => handleEditActivity(activity)} /></td>
                                        <td><img src={trash} className='icon' alt='Icono para eliminar actividad' onClick={() => handleDeleteActivity(activity)} /></td>
                                    </tr>
                                ))
                            ) : (
                                <tr className='flex'>
                                    <td colSpan='7'>No existen aún acciones</td>
                                </tr>
                            )
                            }
                        </tbody>
                        <tfoot className='footer-table flex'>
                            <tr className='flex'>
                                <td colSpan='7'>{`Total Monto PME: ${formatCurrency(calculateTotalAmount())}`}</td>
                            </tr>
                        </tfoot>
                    </table>
                    <span className='box-actions flex'>
                        <button type='button' className='box-button flex' onClick={handleSubmit}>
                            <img src={addPME} className='icon' alt='Icono para crear PME' />
                            <p>{pme?.id ? 'Actualizar PME' : 'Guardar PME'}</p>
                        </button>
                        <p className='button-cancel' onClick={handleClickCancel} >Cancelar</p>
                    </span>
                    {showAddPmeActivities &&
                        <AddPMEActivity
                            setShowPmeActivities={setShowPmeActivities}
                            selectedYear={pmeData.yearPME}
                            onAddActivity={handleAddOrUpdateActivity}
                            initialActivity={editingActivity || viewingActivity}
                            viewMode={!!viewingActivity}
                            onCloseForm={handleCloseForm}
                        />}
                </section>
            }
            {<MessageConfirmation confirmation={globalNotifications.addUpdatedPME} error={error} view={true} />}
            {showErrorDelete && <ErrorPopUp setError={setShowErrorDelete} />}
            {showErrorEdit && <ErrorEditPopUp setError={setShowErrorEdit}/>}
            {showDeleteConfirmation && <DeleteConfirmationPopUp onConfirm={confirmDelete} onCancel={cancelDelete} activityName={activityDelete?.nameOfAction}/>}
        </section >
    )
};

function deepCopyPme(pme) {
    if (!pme) return null;
    return {
        ...pme,
        activities: pme.activities ? pme.activities.map(activity => ({ ...activity })) : []
    };
}

export default PMEForm;